import { useEffect } from 'react';
import { Menu } from '@mantine/core';
import { IconChevronDown, IconLogout, IconUserCircle } from '@tabler/icons-react';
import { getUserName } from '../../../services/authSession';
import {
  clearAuthAndRedirectToLogin,
  getPublicFolderAssetUrl,
} from '../../../services/authNavigation';
import { useCameraContext } from '../../../contexts/CameraContext';
import { CameraPreview } from '../../dashboard/components/CameraPreview';
import classes from './ExamPortalHeader.module.css';

function splitNameLines(name: string): { line1: string; line2: string } {
  const t = name.trim();
  if (!t) return { line1: 'Student', line2: '' };
  const parts = t.split(/\s+/);
  if (parts.length === 1) return { line1: parts[0], line2: '' };
  return { line1: parts[0], line2: parts.slice(1).join(' ') };
}

type Props = {
  /** Kept for backwards compatibility with callers; no longer used. */
  isDashboard?: boolean;
};

export function ExamPortalHeader({ isDashboard: _isDashboard = false }: Props = {}) {
  const { line1, line2 } = splitNameLines(getUserName());
  const { stream, error, isActive, startCamera } = useCameraContext();

  useEffect(() => {
    // Ensure camera starts when header is visible (Dashboard and Exam)
    void startCamera();
  }, [startCamera]);

  return (
    <header className={classes.bar}>
      <div className={classes.left}>
        <img
          src={getPublicFolderAssetUrl('mandal-logo.png')}
          className={classes.logoBadge}
          aria-hidden
        />
        <span className={classes.brand}>JSYOG</span>
      </div>

      <div className={classes.right}>
        <div className={classes.headerCameraContainer}>
          <CameraPreview
            stream={stream}
            error={error}
            isActive={isActive}
            className={classes.headerCamera}
          />
        </div>
        <Menu
          shadow="md"
          width={180}
          position="bottom-end"
          offset={6}
          withinPortal
          zIndex={400}
          transitionProps={{ transition: 'pop-top-right', duration: 120 }}
        >
          <Menu.Target>
            <button
              type="button"
              className={classes.userMenuBtn}
              aria-label="Account menu"
              aria-haspopup="menu"
            >
              <IconUserCircle size={28} stroke={1.8} className={classes.userIcon} aria-hidden />
              <IconChevronDown size={18} stroke={2} className={classes.userMenuChevron} />
            </button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>
              <div className={classes.dropdownName}>
                <span className={classes.dropdownNameLine1}>{line1}</span>
                {line2 ? <span className={classes.dropdownNameLine2}>{line2}</span> : null}
              </div>
            </Menu.Label>
            <Menu.Item
              leftSection={<IconLogout size={16} stroke={2} />}
              onClick={() => clearAuthAndRedirectToLogin('logout')}
            >
              Logout
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>
    </header>
  );
}
